import { useRef, useState } from 'react';

import { UIDAPresentationTypeEnum } from '@kaltura-ott/tvpil';

import { defaultPropsError, ValidatePin } from 'components/widgets/Modal/constants';
import { useParentalPinStore } from 'hooks/common/useParentalPinStore';

import { PinCodeModalTypes } from '../../PinCodeModalTypesEnum';
import { calculatePinLimitTime } from '../../utils/calculatePinLimitTime/calculatePinLimitTime';
import { getPinLimitError } from '../../utils/getPinLimitError/getPinLimitError';
import { useConfirmationErrorMsg } from '../useConfirmationErrorMsg/useConfirmationErrorMsg';
import { useGetPinModalMessage } from '../useGetPinModalMessage/useGetPinModalMessage';
import { usePinCodeModalSubmit } from '../usePinCodeModalSubmit/usePinCodeModalSubmit';
import { useResetErrorMessage } from '../useResetErrorMessage/useResetErrorMessage';
import { useSetAutoFocusAndSubmit } from '../useSetAutoFocusAndSubmit/useSetAutoFocusAndSubmit';

interface Props {
  title: string | undefined;
  description?: string | React.ReactNode;
  isConfirmErrorMessage: boolean;
  type: PinCodeModalTypes;
  context?: UIDAPresentationTypeEnum;
  validatePin: ValidatePin;
  callback?: (success: boolean) => void;
}

export const usePinCodeModalCombiner = ({
  title,
  description,
  isConfirmErrorMessage,
  type,
  context,
  validatePin,
  callback,
}: Props) => {
  const [pinCode, setPinCode] = useState('');
  const [error, setError] = useState<
    | {
        errorMessage: string;
        errorCode: string;
      }
    | undefined
  >(defaultPropsError);
  const [loading, setLoading] = useState(false);
  const inputWrapper = useRef<HTMLDivElement>(null);
  const parentalPinStore = useParentalPinStore();
  const { isParentalPinLocked, parentalPinTimeout, isPurchasePinLocked, purchasePinTimeout } = parentalPinStore;
  const isRestrictedLock =
    [
      PinCodeModalTypes.RestrictedContent,
      PinCodeModalTypes.RestrictedContentParental,
      PinCodeModalTypes.EnterParental,
      PinCodeModalTypes.ParentalRhino,
    ].includes(type) && isParentalPinLocked;
  const isPurchaseLock = PinCodeModalTypes.Purchase === type && isPurchasePinLocked;
  let timeoutValue = 0;

  if (isRestrictedLock) {
    timeoutValue = parentalPinTimeout;
  } else if (isPurchaseLock) {
    timeoutValue = purchasePinTimeout;
  }

  const pinTimeout = calculatePinLimitTime(timeoutValue);
  const isPinLocked = isRestrictedLock || isPurchaseLock;
  const isResetError = !isParentalPinLocked && !isPurchasePinLocked;
  const pinLimitError = isPinLocked ? getPinLimitError(pinTimeout) : undefined;

  const submit = usePinCodeModalSubmit({
    context,
    pinCode,
    pinLimitError,
    setLoading,
    validatePin,
    callback,
    setPinCode,
    setError,
  });

  useSetAutoFocusAndSubmit(pinCode, inputWrapper, submit);

  const { instruction, modalTitle, acceptButtonText } = useGetPinModalMessage(type, title, description);

  useResetErrorMessage(setError, isResetError);

  useConfirmationErrorMsg(isConfirmErrorMessage, setError);

  return {
    error,
    pinCode,
    loading,
    pinTimeout,
    modalTitle,
    instruction,
    inputWrapper,
    pinLimitError,
    acceptButtonText,
    isPurchasePinLocked,
    submit,
    setError,
    setPinCode,
  };
};
